/**
 * @file order-detail.service.ts
 * @module orders-delivery/services/order-detail
 * @description API service for the single-order detail page: fetch, cancel, and invoice download.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.6
 * @version 2.1.2
 */

import APICallService from '../../../../api/apiCallService';
import { ordersDelivery } from '../../../../api/apiEndPoints';
import { OrdersDelivery } from '../../../../utils/constants';

/**
 * Fetches full order details by order ID.
 *
 * @param {string} orderId - Order ID.
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function fetchOrderDetails(
  orderId: string,
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    ordersDelivery.info,
    orderId,
    '',
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Updates an order's status (e.g. cancellation) with an audit log message.
 *
 * @param {string} orderId - Order ID.
 * @param {any} params - Status update payload (status, reason, auditLogMessage, warehouse).
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response
 */
export async function updateOrderStatus(
  orderId: string,
  params: any,
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    ordersDelivery.status,
    params,
    { id: orderId },
    '',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}

/**
 * Downloads the order invoice PDF as a blob.
 *
 * @param {string} orderId - Order ID.
 * @param {string | null} warehouseId - Warehouse ID (optional)
 * @returns {Promise<any>} API response (blob)
 */
export async function downloadOrderInvoice(
  orderId: string,
  warehouseId: string | null
): Promise<any> {
  const apiService = new APICallService(
    ordersDelivery.invoiceDownload,
    orderId,
    undefined,
    'blob',
    '',
    '',
    OrdersDelivery,
    warehouseId || null
  );

  return apiService.callAPI();
}
